import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Compass, AlertTriangle, ShieldAlert, Layers, Star, MapPin, ArrowLeft, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { updateItem } from "../hooks/useEntity";

const GOALS = [
  "Single-family home", "SB 9 development", "SB 1123 development", "Lot split", "Subdivision",
  "Duplex", "Triplex", "Fourplex", "Small-lot development", "Cottage development",
];

const INPUT_FIELDS = [
  { key: "number_of_lots", label: "Proposed Lots", type: "number" },
  { key: "number_of_homes", label: "Proposed Homes", type: "number" },
  { key: "home_sqft", label: "Home Sq Ft", type: "number" },
  { key: "stories", label: "Stories", type: "number" },
  { key: "beds", label: "Bedrooms", type: "number" },
  { key: "baths", label: "Bathrooms", type: "number" },
  { key: "garage_spaces", label: "Garage Spaces", type: "number" },
  { key: "desired_lot_size", label: "Desired Lot Size (SF)", type: "number" },
];

const TOGGLES = [
  { key: "front_loaded_garage", label: "Front garage" },
  { key: "rear_loaded_garage", label: "Rear garage" },
  { key: "alley_access", label: "Alley access" },
  { key: "private_driveway", label: "Private driveway" },
  { key: "common_open_space", label: "Common open space" },
  { key: "pool", label: "Pool" },
  { key: "adu", label: "ADU" },
  { key: "septic_system", label: "Septic" },
];

// Pre-fill the concept form from the record's existing Property Analysis data.
const _num = (v) => { const n = Number(String(v ?? "").replace(/,/g, "").trim()); return Number.isFinite(n) && n > 0 ? n : undefined; };
const _isYes = (v) => v === true || /^\s*(y|yes|true|available)/i.test(String(v ?? ""));
function deriveInputs(record = {}) {
  const out = {};
  const subLots = Array.isArray(record.subdivision?.lots) ? record.subdivision.lots.length : 0;
  const lots = subLots || _num(record.proposed_lots) || _num(record.potential_units);
  if (lots) { out.number_of_lots = lots; out.number_of_homes = lots; }
  const homeSqft = _num(record.living_sqft) || _num(record.approved_living_sqft) || _num(record.be_est_max_living_sqft);
  if (homeSqft) out.home_sqft = homeSqft;
  const stories = _num(record.be_max_stories) || _num(record.lz_max_stories);
  if (stories) out.stories = stories;
  const beds = _num(record.proposed_beds); if (beds) out.beds = beds;
  const baths = _num(record.proposed_baths); if (baths) out.baths = baths;
  const lotSize = _num(record.desired_lot_size) || _num(record.lot_size_sqft);
  if (lotSize) out.desired_lot_size = lotSize;
  if (_isYes(record.lot_utility_septic) || _isYes(record.septic_available)) out.septic_system = true;
  if (_isYes(record.lz_adu_allowed)) out.adu = true;
  return out;
}

const FEAT = {
  driveway: { fill: "#e5e7eb", stroke: "#9ca3af", dash: "", text: "#374151" },
  parking: { fill: "#e5e7eb", stroke: "#9ca3af", dash: "", text: "#374151" },
  yard: { fill: "rgba(34,197,94,0.10)", stroke: "#86909c", dash: "1.5 1.2", text: "#4b5563" },
  common: { fill: "rgba(34,197,94,0.10)", stroke: "#86909c", dash: "1.5 1.2", text: "#4b5563" },
  adu: { fill: "#dfe4ea", stroke: "#111", dash: "", text: "#111" },
  pool: { fill: "#bae6fd", stroke: "#0284c7", dash: "", text: "#0369a1" },
  leach_field: { fill: "none", stroke: "#111", dash: "2 1.4", text: "#111" },
  easement: { fill: "none", stroke: "#111", dash: "4 2", text: "#111" },
  utility_easement: { fill: "none", stroke: "#111", dash: "4 2", text: "#111" },
  slope: { fill: "rgba(148,163,184,0.18)", stroke: "#9ca3af", dash: "1.5 1.2", text: "#4b5563" },
  unbuildable: { fill: "rgba(148,163,184,0.18)", stroke: "#9ca3af", dash: "1.5 1.2", text: "#4b5563" },
  existing: { fill: "#f3f4f6", stroke: "#6b7280", dash: "1.5 1.2", text: "#374151" },
};

function SitePlanSketch({ sketch }) {
  if (!sketch) return null;
  const P = sketch.parcel || {};
  const px = Number(P.x) || 10, py = Number(P.y) || 12, pw = Number(P.w) || 80, ph = Number(P.h) || 72;
  const dims = P.dims || {};
  const buildings = Array.isArray(sketch.buildings) ? sketch.buildings : [];
  const setbacks = Array.isArray(sketch.setbacks) ? sketch.setbacks : [];
  const features = Array.isArray(sketch.features) ? sketch.features : [];
  const legacy = Array.isArray(sketch.elements) ? sketch.elements : [];
  const frontage = sketch.frontage || {};
  const OFF = 5;

  const setLine = (side) => {
    if (side === "top") return { x1: px + 2, y1: py + OFF, x2: px + pw - 2, y2: py + OFF, lx: px + pw / 2, ly: py + OFF + 2 };
    if (side === "bottom") return { x1: px + 2, y1: py + ph - OFF, x2: px + pw - 2, y2: py + ph - OFF, lx: px + pw / 2, ly: py + ph - OFF - 1 };
    if (side === "left") return { x1: px + OFF, y1: py + 2, x2: px + OFF, y2: py + ph - 2, lx: px + OFF + 1, ly: py + ph / 2, rot: 1 };
    return { x1: px + pw - OFF, y1: py + 2, x2: px + pw - OFF, y2: py + ph - 2, lx: px + pw - OFF - 1, ly: py + ph / 2, rot: 1 };
  };

  const treeMark = (f, i, remove) => {
    const cx = Number(f.x) || 0, cy = Number(f.y) || 0, r = 2.4;
    return (
      <g key={`t${i}`}>
        <circle cx={cx} cy={cy} r={r} fill="#fff" stroke="#111" strokeWidth="0.4" />
        <circle cx={cx} cy={cy} r={r * 0.6} fill="none" stroke="#111" strokeWidth="0.25" />
        {remove && <><line x1={cx - r} y1={cy - r} x2={cx + r} y2={cy + r} stroke="#111" strokeWidth="0.5" /><line x1={cx + r} y1={cy - r} x2={cx - r} y2={cy + r} stroke="#111" strokeWidth="0.5" /></>}
        {f.label && <text x={cx} y={cy + r + 2} fontSize="1.7" fill="#111" textAnchor="middle">{f.label}</text>}
      </g>
    );
  };

  const rectFeat = (f, i) => {
    const s = FEAT[f.type] || FEAT.existing;
    const x = Number(f.x) || 0, y = Number(f.y) || 0, w = Number(f.w) || 6, h = Number(f.h) || 6;
    const isTank = f.type === "septic_tank";
    return (
      <g key={`f${i}`}>
        <rect x={x} y={y} width={w} height={h} fill={isTank ? "#111" : s.fill} stroke={isTank ? "#111" : s.stroke} strokeWidth="0.4" strokeDasharray={isTank ? "" : s.dash} />
        {f.type === "leach_field" && [0.2, 0.4, 0.6, 0.8].map((t, k) => (
          <line key={k} x1={x + w * t} y1={y} x2={x + w * t} y2={y + h} stroke="#111" strokeWidth="0.2" strokeDasharray="1 1" />
        ))}
        {f.label && (w > 5 || isTank) && (
          <text x={x + w / 2} y={isTank ? y - 1 : y + h / 2 + 0.7} fontSize="1.7" fill={isTank ? "#111" : s.text} textAnchor="middle">{f.label}</text>
        )}
      </g>
    );
  };

  return (
    <div className="rounded-lg border border-border bg-white p-2" data-testid="site-plan-sketch">
      <svg viewBox="0 0 100 100" className="w-full h-auto" style={{ aspectRatio: "1 / 1" }}>
        <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
        <rect x="1.5" y="1.5" width="97" height="97" fill="none" stroke="#111" strokeWidth="0.4" />

        {/* Parcel boundary */}
        <rect x={px} y={py} width={pw} height={ph} fill="none" stroke="#111" strokeWidth="0.7" />
        {/* Edge dimensions */}
        {dims.top && <text x={px + pw / 2} y={py - 1.5} fontSize="2.2" fontWeight="bold" fill="#111" textAnchor="middle">{dims.top}</text>}
        {dims.bottom && <text x={px + pw / 2} y={py + ph + 3.5} fontSize="2.2" fontWeight="bold" fill="#111" textAnchor="middle">{dims.bottom}</text>}
        {dims.left && <text x={px - 1.5} y={py + ph / 2} fontSize="2.2" fontWeight="bold" fill="#111" textAnchor="middle" transform={`rotate(-90 ${px - 1.5} ${py + ph / 2})`}>{dims.left}</text>}
        {dims.right && <text x={px + pw + 1.5} y={py + ph / 2} fontSize="2.2" fontWeight="bold" fill="#111" textAnchor="middle" transform={`rotate(90 ${px + pw + 1.5} ${py + ph / 2})`}>{dims.right}</text>}

        {/* Setback lines */}
        {setbacks.map((sb, i) => {
          const L = setLine(sb.side || "bottom");
          return (
            <g key={`s${i}`}>
              <line x1={L.x1} y1={L.y1} x2={L.x2} y2={L.y2} stroke="#555" strokeWidth="0.35" strokeDasharray="2 1.3" />
              <text x={L.lx} y={L.ly} fontSize="1.7" fill="#555" textAnchor="middle" transform={L.rot ? `rotate(-90 ${L.lx} ${L.ly})` : undefined}>{sb.label}</text>
            </g>
          );
        })}

        {/* Features */}
        {features.map((f, i) => (["tree_remain", "tree_remove"].includes(f.type) ? treeMark(f, i, f.type === "tree_remove") : rectFeat(f, i)))}
        {buildings.length === 0 && legacy.map((e, i) => rectFeat({ ...e, type: FEAT[e.type] ? e.type : "existing" }, `l${i}`))}

        {/* Buildings */}
        {buildings.map((b, i) => {
          const x = Number(b.x) || 0, y = Number(b.y) || 0, w = Number(b.w) || 10, h = Number(b.h) || 10;
          return (
            <g key={`b${i}`}>
              <rect x={x} y={y} width={w} height={h} fill="#cbd0d6" stroke="#111" strokeWidth="0.6" />
              <text x={x + w / 2} y={y + h / 2 - 0.2} fontSize="2.1" fontWeight="bold" fill="#111" textAnchor="middle">{b.label}</text>
              {b.dims && <text x={x + w / 2} y={y + h / 2 + 2.4} fontSize="1.7" fill="#333" textAnchor="middle">{b.dims}</text>}
            </g>
          );
        })}

        {/* Frontage / street */}
        {frontage.street && (
          <text x={frontage.side === "left" ? px - 5 : frontage.side === "right" ? px + pw + 5 : px + pw / 2}
            y={frontage.side === "top" ? py - 5 : py + ph + 7.5}
            fontSize="2.4" fontStyle="italic" fontWeight="bold" fill="#111" textAnchor="middle"
            transform={frontage.side === "left" ? `rotate(-90 ${px - 5} ${py + ph / 2})` : frontage.side === "right" ? `rotate(90 ${px + pw + 5} ${py + ph / 2})` : undefined}>
            {frontage.street}{frontage.row ? `   ·   ${frontage.row}` : ""}
          </text>
        )}

        {/* North arrow */}
        <g transform="translate(94,7)">
          <line x1="0" y1="4" x2="0" y2="-2" stroke="#111" strokeWidth="0.4" />
          <polygon points="0,-4 -1.3,-1 1.3,-1" fill="#111" />
          <text x="0" y="8" fontSize="2.4" fill="#111" textAnchor="middle" fontWeight="bold">N</text>
        </g>
      </svg>
      {sketch.scale_note && <div className="text-[10px] text-gray-500 mt-1 text-center">{sketch.scale_note}</div>}
    </div>
  );
}

const FEAS_CLS = { Strong: "text-primary", Moderate: "text-amber-400", Weak: "text-destructive", Unknown: "text-muted-foreground" };

function OptionView({ opt, onRender, rendering }) {
  const s = opt.summary || {};
  const env = opt.envelope || {};
  const feas = opt.feasibility || {};
  const money = (v) => (typeof v === "number" ? `$${v.toLocaleString()}` : v || "—");
  const sf = (v) => (v || v === 0 ? `${Number(v).toLocaleString()} SF` : "—");
  return (
    <div className="space-y-4" data-testid={`concept-option-${opt.key}`}>
      {opt.strategy && <p className="text-[13px] text-muted-foreground">{opt.strategy}</p>}

      <div data-testid="concept-ai-render">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">AI Site-Plan Render</div>
          <Button size="sm" variant="outline" onClick={onRender} disabled={rendering} data-testid="concept-render-btn">
            {rendering ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Rendering…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />{opt.render_url ? "Regenerate render" : "Generate AI render"}</>}
          </Button>
        </div>
        {opt.render_url ? (
          <img src={opt.render_url} alt="AI site plan render" className="w-full rounded-lg border border-border" data-testid="concept-render-img" />
        ) : rendering ? (
          <div className="flex items-center justify-center gap-2 text-muted-foreground text-[13px] rounded-lg border border-dashed border-border py-10"><Loader2 className="h-4 w-4 animate-spin" />OpenAI is drawing the site plan… (~30–60s)</div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Development Summary</div>
            <div className="grid grid-cols-2 gap-2 text-[13px]">
              {[["Parcel", sf(s.parcel_area_sf)], ["Proposed Lots", s.proposed_lots ?? "—"], ["Proposed Homes", s.proposed_homes ?? "—"], ["Avg Lot", sf(s.avg_lot_sf)], ["Home Size", sf(s.home_sf)], ["Total Sellable", sf(s.total_sellable_sf)], ["Garage", s.garage || "—"], ["Parking", s.parking_spaces ? `${s.parking_spaces} spaces` : "—"], ["Density", s.density_du_acre ? `${s.density_du_acre} DU/ac` : "—"], ["Private Yard", s.private_yard ? "Yes" : "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border/40 py-1"><span className="text-muted-foreground">{k}</span><span className="text-foreground font-medium">{v}</span></div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Buildable Envelope</div>
            <div className="grid grid-cols-2 gap-2 text-[13px]">
              {[["Gross Area", sf(env.gross_sf)], ["Unbuildable", sf(env.unbuildable_sf)], ["Buildable", sf(env.buildable_sf)], ["Footprint", sf(env.footprint_sf)], ["Lot Coverage", env.coverage_pct != null ? `${env.coverage_pct}%` : "—"], ["Max Coverage", env.max_coverage_pct != null ? `${env.max_coverage_pct}%` : "Needs Verification"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border/40 py-1"><span className="text-muted-foreground">{k}</span><span className="text-foreground font-medium">{v}</span></div>
              ))}
            </div>
            {env.warning && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[12px] text-amber-300" data-testid="concept-zoning-warning">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{env.warning}
              </div>
            )}
          </div>
        </div>
      </div>

      {Array.isArray(opt.lots) && opt.lots.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
              <th className="py-1.5 pr-3">Lot</th><th className="pr-3">Size</th><th className="pr-3">Dims</th><th className="pr-3">Home</th><th className="pr-3">Footprint</th><th className="pr-3">Setbacks</th><th>Confidence</th>
            </tr></thead>
            <tbody>
              {opt.lots.map((l, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td className="py-1.5 pr-3 text-foreground font-medium">{l.name}</td>
                  <td className="pr-3">{l.lot_sf ? `${Number(l.lot_sf).toLocaleString()} SF` : "—"}</td>
                  <td className="pr-3">{l.dimensions || "—"}</td>
                  <td className="pr-3">{l.home_sf ? `${Number(l.home_sf).toLocaleString()} SF${l.stories ? ` · ${l.stories}-story` : ""}` : "—"}</td>
                  <td className="pr-3">{l.footprint_sf ? `${Number(l.footprint_sf).toLocaleString()} SF` : "—"}</td>
                  <td className="pr-3">{l.setbacks ? `F ${l.setbacks.front || "?"} · S ${l.setbacks.side || "?"} · R ${l.setbacks.rear || "?"}` : "—"}</td>
                  <td><span className={l.confidence === "Verified" ? "text-primary" : l.confidence === "Estimated" ? "text-amber-400" : "text-destructive"}>{l.confidence || "Needs Verification"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Feasibility</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
            {Object.entries(feas).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span><span className={`font-semibold ${FEAS_CLS[v] || "text-muted-foreground"}`}>{v}</span></div>
            ))}
          </div>
          {(opt.financials?.estimated_value || opt.financials?.estimated_profit) && (
            <div className="mt-3 flex gap-4 text-[13px]">
              {opt.financials.estimated_value && <div><span className="text-muted-foreground text-[10px] uppercase block">Est. Value</span><span className="text-foreground font-semibold">{money(opt.financials.estimated_value)}</span></div>}
              {opt.financials.estimated_profit && <div><span className="text-muted-foreground text-[10px] uppercase block">Est. Profit</span><span className="text-primary font-semibold">{money(opt.financials.estimated_profit)}</span></div>}
            </div>
          )}
        </div>
        {Array.isArray(opt.risks) && opt.risks.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Development Risks</div>
            <ul className="space-y-1 text-[12px]">
              {opt.risks.map((r, i) => (<li key={i} className="flex gap-1.5 text-muted-foreground"><span className="text-amber-400">•</span>{r}</li>))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Guardrail() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[12px] text-amber-200" data-testid="concept-guardrail">
      <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
      <span><strong>Preliminary Concept — Not for Construction.</strong> For early feasibility only. Parcel boundaries, setbacks, easements, utilities, grading, drainage, septic feasibility and building locations must be verified by licensed professionals and government agencies before acquisition, entitlement, design or construction.</span>
    </div>
  );
}

export function ConceptSitePlanWorkspace({ record, resource, id, onSaved }) {
  const existing = record.concept_site_plan || null;
  const [goal, setGoal] = useState(existing?.development_goal || record.development_type || GOALS[0]);
  const [inputs, setInputs] = useState(existing?.inputs || deriveInputs(record));
  const [busy, setBusy] = useState(false);
  const [concept, setConcept] = useState(existing);
  const [active, setActive] = useState(existing?.options?.[0]?.key || "A");
  const [view, setView] = useState(existing ? "results" : "form");

  const setField = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
  const toggle = (k) => setInputs((p) => ({ ...p, [k]: !p[k] }));

  const applyResult = (rec, job) => {
    const c = rec.concept_site_plan;
    setConcept(c);
    setActive(c.options?.[0]?.key || "A");
    setView("results");
    onSaved && onSaved((prev) => ({ ...prev, concept_site_plan: c, concept_site_plan_job: job }));
  };

  const pollResult = async () => {
    // Background job can exceed the 60s gateway limit; poll the record until it's ready/errored.
    for (let i = 0; i < 130; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      let rec;
      try { rec = (await api.get(`/${resource}/${id}`)).data; } catch { continue; }
      const job = rec.concept_site_plan_job || {};
      if (job.status === "ready" && rec.concept_site_plan) { applyResult(rec, job); toast.success("Concept plan ready"); return true; }
      if (job.status === "error") { toast.error(job.error || "Angela could not generate a concept plan. Please try again."); return true; }
    }
    return false;
  };

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/ai/concept-site-plan", { resource, record_id: id, development_goal: goal, inputs });
      const done = await pollResult();
      if (!done) toast.message("Angela is still sketching — you can close this; your concept will appear here and on the record as soon as it's ready.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Resume polling if a generation is already running when the page loads.
  useEffect(() => {
    if (!busy && (record.concept_site_plan_job || {}).status === "generating") {
      setBusy(true);
      pollResult().finally(() => setBusy(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markPreferred = async (key) => {
    const updated = { ...concept, preferred_key: key };
    setConcept(updated);
    try {
      await updateItem(resource, id, { concept_site_plan: updated });
      onSaved && onSaved((prev) => ({ ...prev, concept_site_plan: updated }));
      toast.success(`Option ${key} marked as Preferred Concept`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [rendering, setRendering] = useState(null);
  const renderImage = async (key) => {
    setRendering(key);
    try {
      await api.post("/ai/concept-site-plan/render", { resource, record_id: id, option_key: key });
      for (let i = 0; i < 65; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        let rec;
        try { rec = (await api.get(`/${resource}/${id}`)).data; } catch { continue; }
        const c = rec.concept_site_plan;
        const o = (c?.options || []).find((x) => x.key === key);
        if (o?.render_url) { setConcept(c); onSaved && onSaved((prev) => ({ ...prev, concept_site_plan: c })); break; }
        if (o?.render_status === "error") { toast.error(o.render_error || "Image render failed. Please try again."); break; }
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRendering(null);
    }
  };

  const options = concept?.options || [];
  const activeOpt = options.find((o) => o.key === active) || options[0];

  return (
    <div data-testid="concept-site-plan-page">

        {view === "form" ? (
          <div className="space-y-5">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Development Goal</div>
              <div className="flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <button key={g} onClick={() => setGoal(g)} data-testid={`concept-goal-${g.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`text-[13px] rounded-full px-3 py-1.5 border transition-colors ${goal === g ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{g}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Your Inputs <span className="normal-case tracking-normal text-muted-foreground/60">· all optional — Angela fills the rest and flags assumptions</span></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {INPUT_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">{f.label}</label>
                    <Input type="number" value={inputs[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} className="h-8 text-sm mt-0.5" data-testid={`concept-input-${f.key}`} />
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {TOGGLES.map((t) => (
                  <button key={t.key} onClick={() => toggle(t.key)} data-testid={`concept-toggle-${t.key}`}
                    className={`text-[12px] rounded-full px-3 py-1 border transition-colors ${inputs[t.key] ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={generate} disabled={busy} data-testid="concept-generate-btn">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Angela is sketching options…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Concept Plan</>}
              </Button>
            </div>
            {busy && (
              <p className="text-[12px] text-muted-foreground text-right -mt-3">
                This can take a couple of minutes. You can close this window — Angela keeps working and your concept appears here and on the record when ready.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-foreground">{(options[0] || {}).label || "Concept Site Plan"}</div>
              <Button variant="outline" size="sm" onClick={() => setView("form")} data-testid="concept-regenerate-btn"><Sparkles className="h-3.5 w-3.5 mr-1.5" />Regenerate</Button>
            </div>

            <Guardrail />

            {options[0] ? <OptionView opt={options[0]} onRender={() => renderImage(options[0].key)} rendering={rendering === options[0].key} /> : null}

            {(concept.assumptions?.length > 0 || concept.needs_verification?.length > 0) && active !== "__compare" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                {concept.assumptions?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Assumptions</div>
                    <ul className="space-y-1 text-[12px]">{concept.assumptions.map((a, i) => (<li key={i} className="flex gap-1.5 text-muted-foreground"><span className="text-primary">•</span>{a}</li>))}</ul>
                  </div>
                )}
                {concept.needs_verification?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Needs Verification</div>
                    <ul className="space-y-1 text-[12px]">{concept.needs_verification.map((a, i) => (<li key={i} className="flex gap-1.5 text-muted-foreground"><MapPin className="h-3 w-3 shrink-0 mt-0.5 text-amber-400" />{a}</li>))}</ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
    </div>
  );
}

export default function ConceptSitePlanPage() {
  const { resource, id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/${resource}/${id}`).then((r) => { if (alive) { setRecord(r.data); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [resource, id]);

  const title = record?.property_name || record?.property_analysis_name || record?.first_name || "Property Analysis";

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-6" data-testid="concept-site-plan-route">
      <button onClick={() => navigate(`/record/${resource}/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="concept-back-btn">
        <ArrowLeft className="h-4 w-4" />Back to {title}
      </button>
      <div className="flex items-start gap-3 mb-6">
        <span className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Compass className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Concept Site Plan</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Angela sketches how this parcel could be developed using your property analysis data. Preliminary feasibility only.</p>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" />Loading property…</div>
      ) : record ? (
        <ConceptSitePlanWorkspace record={record} resource={resource} id={id} onSaved={setRecord} />
      ) : (
        <div className="text-muted-foreground py-16 text-center">Record not found.</div>
      )}
    </div>
  );
}

export function ConceptSitePlanCard({ record, onOpen }) {
  const [open, setOpen] = useState(false);
  const c = record.concept_site_plan;
  if (!c || !Array.isArray(c.options) || c.options.length === 0) return null;
  const o = c.options.find((x) => x.key === c.preferred_key) || c.options[0];
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="concept-site-plan-card">
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-accent/40 transition-colors" onClick={() => setOpen((v) => !v)} data-testid="concept-card-toggle">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Concept Site Plan</h3>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid="concept-open-btn"><Compass className="h-3.5 w-3.5 mr-1.5" />Open</Button>
          <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {open && (
        <div className="px-5 py-4 border-t border-border">
          <div className="text-[11px] text-muted-foreground mb-3">Goal: <span className="text-foreground">{c.development_goal}</span></div>
          <button onClick={onOpen} className="w-full text-left rounded-lg border border-primary/40 bg-primary/[0.05] px-4 py-3 hover:border-primary/60 transition-colors" data-testid="concept-card-option">
            <div className="text-sm font-semibold text-foreground">{o.label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{o.summary?.proposed_homes ?? "?"} homes · {o.summary?.avg_lot_sf ? `${o.summary.avg_lot_sf.toLocaleString()} SF avg lot` : "—"}</div>
          </button>
        </div>
      )}
    </div>
  );
}
